import { CREATE_NOTICE } from "../../routes";
import type { WizardHandover } from "../notices/wizardSession";

/**
 * Asking Ask Noticeboard to write or reword text ("make this sound friendlier").
 * It doesn't write Notice text, so these get one fixed reply pointing to the
 * wizard's own AI button. Starting a Notice ("write a notice reminding
 * parents…") is a task, not one of these. Translation is checked separately
 * with `isTranslationRequest`: Rewrite with Nova reworks wording, it doesn't
 * change language, so it needs its own honest reply rather than this one.
 */
const WRITING_REQUESTS = [
  /\b(re-?write|re-?word|re-?phrase|paraphrase|proof-?read)\b/,
  /\b(turn|convert|change) (this|it|that|my)\b.*\binto\b/,
  /\bmake (this|it|that|(my|the|our|this) (notice|message|text|email|letter|wording))\b.*\b(sound|friendlier|warmer|nicer|kinder|shorter|longer|clearer|simpler|formal|polite|better|more|less)\b/,
  /\b(fix|check|correct|improve) (the |my |this )?(grammar|spelling|wording|tone|writing)\b/,
  /\bwrite (the |my )?(body|text|wording|words|content)\b/,
  /\bwrite (this|it|that)( up)? for me\b/,
  // "can you write the notice for me? its about the swimming gala": the words wanted, not a blank draft
  /\b(can|could|would|will) you write (the|my|this|a) (notice|message|announcement)( up)? for (me|us)\b/,
  /\b(shorten|summari[sz]e|simplify|polish) (this|it|that|my)\b/,
  // "Can you help me word it…", "could you suggest gentle wording for a head-lice notice?"
  /\bhelp me (word|phrase|write)\b/,
  /\bsuggest\b[^.?!]*\b(wording|words|phrasing|text|message)\b/,
  /\bmake it sound\b/,
];

export function isWritingRequest(text: string): boolean {
  const lower = text.toLowerCase();
  if (isTeachingWork(lower)) return false;
  return WRITING_REQUESTS.some((pattern) => pattern.test(lower));
}

/** "my lesson plan for fractions", "report comments", "a test for Grade 4": teaching work, not a Notice. */
const TEACHING_WORK = /\b(?:lesson\s+plans?|schemes?\s+of\s+work|report\s+(?:card\s+)?comments?|marking|mark\s+(?:the|my|these|their)\s+(?:(?:learners?'?|students?'?|kids'?|class'?)\s+)?(?:tests?|scripts|papers|essays?|assignments?|projects?|homework|books)|exam\s+papers?|tests?\s+for\s+(?:my\s+)?(?:class|grade)|homework\s+sheets?)\b/i;
export const isTeachingWork = (text: string) => TEACHING_WORK.test(text);

export const TEACHING_WORK_REPLY =
  "I can't help with lesson plans or marking: I only know about Noticeboard. For ready-made worksheets, have a look in the Library.";

const SA_LANGUAGE =
  "(?:isi)?(?:zulu|xhosa|afrikaans|sotho|sesotho|tswana|setswana|venda|tshivenda|tsonga|xitsonga|swati|siswati|ndebele|isindebele)";

export function isTranslationRequest(text: string): boolean {
  const lower = text.toLowerCase();
  if (/\btranslat(e|ion|ing)\b/.test(lower)) return true;
  // "say this in Zulu", "convert this to Afrikaans": a language, without the word translate.
  return new RegExp(`\\b(?:in|into|to)\\s+${SA_LANGUAGE}\\b`, "i").test(lower);
}

export const WRITING_REQUEST_REPLY =
  "I don't write Notice text, but the wizard can help. Open a new Notice, write or paste your text, and press Rewrite with Nova. Nothing is sent until you publish it.";

/** Said when they can't open Create, so there's no wizard to offer. */
export const WRITING_REQUEST_REPLY_NO_WIZARD =
  "I don't write Notice text. Whoever sends your Notices can use Rewrite with Nova in the wizard.";

/** Rewrite with Nova reworks wording; it doesn't change language. Never implies otherwise. */
export const TRANSLATION_REQUEST_REPLY =
  "Noticeboard doesn't translate notices. Write or paste the translated text yourself on the writing step — Rewrite with Nova can still help with tone, just not the language.";

/** Said when they can't open Create, so there's no wizard to offer. */
export const TRANSLATION_REQUEST_REPLY_NO_WIZARD =
  "Noticeboard doesn't translate notices. Whoever sends your Notices will need to write or paste the translated text themselves.";

/**
 * A new, empty Information notice in the wizard: nothing from the chat is put in it.
 * Built here rather than with create.ts's helper, which loads date-fns locales Jest can't parse.
 */
export const toNewNotice = () => ({
  pathname: CREATE_NOTICE,
  state: { prefillNotice: { noticeType: "info" } } as WizardHandover,
});

/** Image upload stays in the composer; keep a supplied title without selecting an audience. */
export const toNewImageNotice = (title = "") => ({
  pathname: CREATE_NOTICE,
  state: { prefillNotice: { noticeType: "image_notice", startAt: "title", initialFormData: { title, audience: "" } } } as WizardHandover,
});
